import { useQuery } from '@tanstack/react-query';
import { listAdminTransactions, getAdminTransaction, getTransactionDetail, getTransactionTrace } from '@/lib/api/admin-transactions';
import type { ListAdminTransactionsParams, TransactionStatus } from '@/types/admin-transaction';

export const adminTransactionKeys = {
  all: ['admin-transactions'] as const,
  lists: () => [...adminTransactionKeys.all, 'list'] as const,
  list: (params?: ListAdminTransactionsParams) =>
    [...adminTransactionKeys.lists(), params] as const,
  details: () => [...adminTransactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminTransactionKeys.details(), id] as const,
  // Spec 141: Eigener Namespace fuer Timeline-Detail (Key-Collision mit detail() vermeiden)
  timelineDetails: () => [...adminTransactionKeys.all, 'timeline-detail'] as const,
  timelineDetail: (id: string) => [...adminTransactionKeys.timelineDetails(), id] as const,
  // Spec 143: Getrennte Keys fuer Basis-Trace und CW-Trace, damit die
  // Phase-1-Daten beim CW-Nachladen sichtbar bleiben
  traces: () => [...adminTransactionKeys.all, 'trace'] as const,
  trace: (id: string) => [...adminTransactionKeys.traces(), id] as const,
  tracesCloudWatch: () => [...adminTransactionKeys.all, 'trace-cw'] as const,
  traceCloudWatch: (id: string) => [...adminTransactionKeys.tracesCloudWatch(), id] as const,
};

const TERMINAL_STATUSES: TransactionStatus[] = [
  'charged', 'renewed', 'failed', 'cancelled', 'expired',
];

export function useAdminTransactions(params?: ListAdminTransactionsParams) {
  // Spec 475: aktive ID-Suche → auch die React-Query-Schicht darf nicht retrien.
  // Das noRetry in listAdminTransactions deckt nur die HTTP-Client-Schicht ab;
  // der App-QueryClient-Default (retry: 1, App.tsx) wuerde die queryFn bei einem
  // 504 sonst ein zweites Mal ausfuehren — 2x ~29-s-Facade-Tabellen-Scan, genau
  // die Kaskade, die verhindert werden soll. Beide Schichten explizit setzen
  // (Muster useTransactionTraceCloudWatch unten). Normales Listen-Laden ohne
  // Suche behaelt den 1-Retry-Komfort des App-Defaults.
  const searchActive = !!(
    params?.txId ||
    params?.internetqTransactionId ||
    params?.customerReference ||
    params?.customerReferenceExact
  );
  return useQuery({
    queryKey: adminTransactionKeys.list(params),
    queryFn: () => listAdminTransactions(params),
    staleTime: 30_000,
    ...(searchActive ? { retry: false } : {}),
  });
}

/**
 * Hook to fetch a single transaction by ID.
 * Spec 0027: Optional merchantId hint for platform_admin Facade routing.
 * Query-Key stays without merchantId — same data regardless of routing path.
 */
export function useAdminTransaction(transactionId: string | undefined, merchantId?: string) {
  return useQuery({
    queryKey: adminTransactionKeys.detail(transactionId ?? ''),
    queryFn: () => getAdminTransaction(transactionId!, merchantId),
    enabled: !!transactionId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && !TERMINAL_STATUSES.includes(status)) return 5_000;
      return false;
    },
  });
}

/**
 * Hook to fetch transaction detail with timeline.
 * Spec 141: Fetches from BFF GET /admin/transactions/{id}/detail
 * Kein refetchInterval — Timeline-Daten sind historisch.
 */
export function useTransactionDetail(transactionId: string | undefined) {
  return useQuery({
    queryKey: adminTransactionKeys.timelineDetail(transactionId ?? ''),
    queryFn: () => getTransactionDetail(transactionId!),
    enabled: !!transactionId,
    staleTime: 30_000,
  });
}

/**
 * Hook fuer den Basis-Trace (Spec 143, ohne CloudWatch).
 * Lazy: feuert erst wenn `enabled` true ist (= Trace-Tab aktiv).
 * Kein refetchInterval — Trace-Daten sind historisch.
 */
export function useTransactionTrace(transactionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: adminTransactionKeys.trace(transactionId ?? ''),
    queryFn: () => getTransactionTrace(transactionId!),
    enabled: !!transactionId && enabled,
    staleTime: 30_000,
  });
}

/**
 * Hook fuer den Trace MIT CloudWatch-Korrelation (Spec 143, ?include=cloudwatch).
 * Opt-in: feuert erst wenn `enabled` true ist (= CW-Toggle gedrueckt).
 *
 * Die CW-Query ist teuer (2-15s, facade-seitige CloudWatch-Insights-Abfrage):
 * - staleTime Infinity + retry false → NIE automatisch wiederholen (Spec Trap §8.9)
 * - Manuelles Neuladen ist Teil des Hook-Vertrags: `refetch()` umgeht staleTime
 *   per React-Query-Design und ist der EINZIGE Weg zur erneuten Query —
 *   dafuer gedacht: "erneut laden" bei CLOUDWATCH_LOGS_DELAYED und Retry im
 *   CW-Fehlerfall (Spec §5.4). retry: false betrifft nur automatische Retries.
 */
export function useTransactionTraceCloudWatch(transactionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: adminTransactionKeys.traceCloudWatch(transactionId ?? ''),
    queryFn: () => getTransactionTrace(transactionId!, { includeCloudWatch: true }),
    enabled: !!transactionId && enabled,
    staleTime: Infinity,
    retry: false,
  });
}
